<template>
  <div class="login-page">
    <div v-if="checkingSession" class="login-container">
      <div class="login-title">Pixie Properties</div>
      <p>Checking session…</p>
    </div>
    <div class="login-container" v-else>
      <div class="login-title">Pixie Properties 2024</div>
      <h2>Welcome! Login here.</h2>
      <form @submit.prevent="login" v-if="!showPasswordReset">
        <div>
          <label for="username">Username:</label>
          <input
            type="text"
            v-model="username"
            required
            :disabled="isLoggingIn"
            autocomplete="username"
          />
        </div>
        <div>
          <label for="password">Password:</label>
          <input
            type="password"
            v-model="password"
            required
            :disabled="isLoggingIn"
            autocomplete="current-password"
          />
        </div>
        <div class="login-submit-row">
          <button
            type="submit"
            class="login-submit-btn"
            :class="{ 'is-loading': isLoggingIn }"
            :disabled="isLoggingIn"
          >
            Login
          </button>
          <span
            v-if="isLoggingIn"
            class="login-spinner"
            aria-label="Signing in"
            role="status"
          >
            <i class="fas fa-spinner fa-spin"></i>
          </span>
        </div>
      </form>

      <!-- Password Reset Form -->
      <form @submit.prevent="resetPassword" v-if="showPasswordReset">
        <h3>Password Reset Required</h3>
        <div>
          <label for="currentPassword">Current Password:</label>
          <input type="password" v-model="currentPassword" required />
        </div>
        <div>
          <label for="newPassword">New Password:</label>
          <input type="password" v-model="newPassword" required />
        </div>
        <div>
          <label for="confirmPassword">Confirm New Password:</label>
          <input type="password" v-model="confirmPassword" required />
        </div>
        <button type="submit" :disabled="isResetting">
          {{ isResetting ? 'Updating...' : 'Update Password' }}
        </button>
        <button type="button" @click="cancelReset" class="cancel-button">
          Cancel
        </button>
      </form>

      <div v-if="error" class="error">{{ error }}</div>
      <div v-if="successMessage" class="success">{{ successMessage }}</div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig'; // Adjust based on your axios configuration
import CryptoJS from 'crypto-js'; // Import CryptoJS for password hashing
import { getDefaultHomePath } from '@/utils/deviceHome';
import { loginFailureMessage } from '@/utils/loginFailureMessage';
import { clearStaffSession, loggedOutQuery } from '@/utils/staffLogout';

export default {
  name: 'LoginPage',
  data() {
    return {
      username: '',
      password: '',
      error: null,
      successMessage: null,
      showPasswordReset: false,
      resetUserId: null,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      isResetting: false,
      isLoggingIn: false,
      checkingSession: true,
    };
  },
  async created() {
    if (loggedOutQuery(this.$route.query)) {
      clearStaffSession(localStorage, sessionStorage);
      delete axios.defaults.headers.common['Authorization'];
      this.checkingSession = false;
      return;
    }
    // Already logged in (PWA reopen): skip the form and go home
    const token = localStorage.getItem('token');
    if (!token) {
      this.checkingSession = false;
      return;
    }
    try {
      const response = await axios.get('/api/validate-token', {
        timeout: 12000,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.valid && response.data?.user) {
        const redirectPath = sessionStorage.getItem('redirectAfterLogin');
        sessionStorage.removeItem('redirectAfterLogin');
        await this.$router.replace(redirectPath || getDefaultHomePath());
        return;
      }
    } catch (_) {
      localStorage.removeItem('token');
    }
    this.checkingSession = false;
  },
  methods: {
    async login() {
      if (this.isLoggingIn) return;
      this.isLoggingIn = true;
      try {
        this.error = null;
        this.successMessage = null;

        // Clear any existing token to prevent conflicts
        localStorage.removeItem('token');

        // Capture the screen dimensions
        const screenX = window.innerWidth;
        const screenY = window.innerHeight;

        // Create a timestamp to prevent replay attacks
        const timestamp = new Date().getTime();

        // Hash the password with SHA256 (without appending the timestamp)
        // This creates a consistent hash that can be bcrypted on the server
        const hashedPassword = CryptoJS.SHA256(this.password).toString();

        console.log('Login attempt for user:', this.username);
        console.log('Hashed password length:', hashedPassword.length);

        // Make the login request with hashed password
        const response = await axios.post('/api/login', {
          username: this.username,
          password: hashedPassword,
          timestamp: timestamp, // Send timestamp for validation
          screenX: screenX, // Add screenX to the request
          screenY: screenY, // Add screenY to the request
        });

        console.log('Login response:', response.data);

        // Check if password reset is required
        if (response.data.requirePasswordReset) {
          this.showPasswordReset = true;
          this.resetUserId = response.data.userId;
          this.currentPassword = this.password; // Pre-fill current password
          this.error = null;
          return;
        }

        // Store the token if the login is successful
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          console.log('Token stored successfully');
          // Prefer post-login destination; default to mobile or desktop home
          const redirectPath = sessionStorage.getItem('redirectAfterLogin');
          sessionStorage.removeItem('redirectAfterLogin');
          // Keep spinner until navigation completes
          await this.$router.push(redirectPath || getDefaultHomePath());
          return;
        } else {
          this.error = 'Login successful but no token received';
        }
      } catch (error) {
        console.log(
          'Login error:',
          error.response ? error.response.data : error,
        );
        this.error = loginFailureMessage(error);
      } finally {
        this.isLoggingIn = false;
      }
    },

    async resetPassword() {
      try {
        this.error = null;
        this.successMessage = null;

        // Validate passwords match
        if (this.newPassword !== this.confirmPassword) {
          this.error = 'New passwords do not match';
          return;
        }

        // Validate password strength (basic check)
        if (this.newPassword.length < 6) {
          this.error = 'New password must be at least 6 characters long';
          return;
        }

        this.isResetting = true;

        // Hash both current and new passwords with SHA256
        const hashedCurrentPassword = CryptoJS.SHA256(
          this.currentPassword,
        ).toString();
        const hashedNewPassword = CryptoJS.SHA256(this.newPassword).toString();

        // Call the reset-required-password endpoint
        const response = await axios.post('/api/reset-required-password', {
          userId: this.resetUserId,
          currentPassword: hashedCurrentPassword,
          newPassword: hashedNewPassword,
        });

        if (response.data.success) {
          this.successMessage =
            'Password updated successfully! Please log in with your new password.';

          // Clear form fields
          this.currentPassword = '';
          this.newPassword = '';
          this.confirmPassword = '';

          // Wait a moment to show success message, then return to login form
          setTimeout(() => {
            this.showPasswordReset = false;
            this.resetUserId = null;
            this.username = ''; // Clear username so user can re-enter
            this.password = ''; // Clear password field
            this.successMessage = null; // Clear success message
          }, 3000);
        }
      } catch (error) {
        console.error('Password reset error:', error);
        this.error =
          error.response?.data?.message ||
          'An error occurred while updating your password';
      } finally {
        this.isResetting = false;
      }
    },

    cancelReset() {
      this.showPasswordReset = false;
      this.resetUserId = null;
      this.currentPassword = '';
      this.newPassword = '';
      this.confirmPassword = '';
      this.error = null;
      this.successMessage = null;
    },
  },
};
</script>

<style scoped>
.login-page {
  height: 100vh;
  /* Relative to this file so css-loader can resolve it (absolute /… urls break the build) */
  background: url('../../public/compressed/logo.webp') no-repeat center center
    fixed;
  background-size: cover;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
}

.login-container {
  background-color: rgba(255, 255, 255, 0.8);
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 310px;
  width: 100%;
}

.login-title {
  font-weight: 800;
  font-size: 2.3em;
  color: #846099;
  margin-top: -20px;
  font-stretch: condensed;
}

.login-container h2 {
  margin-bottom: 1rem;
}

.login-container form {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.login-container form > div {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.login-container label {
  margin-bottom: 0.3rem;
  margin-right: 0px;
  min-width: 150px;
}

.login-container input {
  margin-bottom: 1rem;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
  box-sizing: border-box;
}

.login-submit-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 0.25rem;
}

.login-container button {
  padding: 0.5rem;
  border: none;
  border-radius: 5px;
  background-color: #9c72b5;
  color: white;
  cursor: pointer;
  max-width: 185px;
  margin-left: 0px;
  padding: 12px;
  transition:
    opacity 0.25s ease,
    background-color 0.2s ease;
}

.login-submit-btn {
  min-width: 120px;
}

.login-submit-btn.is-loading,
.login-submit-btn:disabled {
  opacity: 0.35;
  cursor: wait;
  pointer-events: none;
}

.login-container button:hover:not(:disabled) {
  background-color: #4a148c;
}

.login-spinner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #846099;
  font-size: 1.35rem;
  line-height: 1;
}

.login-container input:disabled {
  opacity: 0.7;
  cursor: wait;
  background: #f5f5f5;
}

.error {
  color: red;
  margin-top: 1rem;
}

.success {
  color: green;
  margin-top: 1rem;
  font-weight: bold;
}

.cancel-button {
  background-color: #6c757d !important;
  margin-top: 0.5rem;
  margin-left: 0px !important;
}

.cancel-button:hover {
  background-color: #5a6268 !important;
}
</style>
